'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Clock, Eye } from 'lucide-react';
import { Header } from '../../Header';
import { Footer } from '../../Footer';
import { LoginModal } from '../../LoginModal';
import { TiptapRenderer } from '../../blog/TiptapRenderer';
import { LikeButton } from '../../blog/LikeButton';
import { SocialIconLinks } from '../../blog/SocialIconLinks';
import { CommentSection } from '../../blog/CommentSection';
import { PostCard } from '../../blog/PostCard';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { ImageWithFallback } from '../../figma/ImageWithFallback';
import { PageType, blogCategoryPath } from '../../Router';
import { trackEvent } from '../../../lib/analytics';
import { incrementViewCount, getRelatedPosts, type BlogPostDocument } from '../../../firebase/blog-db';

interface BlogArticlePageProps {
  post: BlogPostDocument;
  onNavigate: (page: PageType) => void;
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export function BlogArticlePage({ post, onNavigate }: BlogArticlePageProps) {
  const [showLogin, setShowLogin] = useState(false);
  const [related, setRelated] = useState<BlogPostDocument[]>([]);

  useEffect(() => {
    if (post.id) incrementViewCount(post.id).catch(() => {});
    trackEvent('article_view', { slug: post.slug, category: post.categorySlug });
    getRelatedPosts(post).then(setRelated).catch(() => {});
  }, [post.id]);

  return (
    <div className="min-h-screen bg-white">
      <Header onNavigate={onNavigate} />
      <main className="pt-14">
        <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <Link href={blogCategoryPath(post.categorySlug)} className="eyebrow text-hustlance-primary hover:text-hustlance-secondary transition-colors">
            {post.categoryName}
          </Link>

          <h1 className="font-editorial text-4xl sm:text-6xl text-gray-900 leading-[1.05] mt-4 mb-6">{post.title}</h1>
          <p className="text-xl text-gray-500 leading-relaxed mb-10">{post.excerpt}</p>

          <div className="flex flex-wrap items-center gap-4 pb-10 border-b border-gray-100">
            <Avatar className="h-11 w-11">
              <AvatarImage src={post.authorAvatarUrl} alt={post.authorName} />
              <AvatarFallback className="bg-gradient-to-r from-hustlance-primary to-hustlance-secondary text-white">
                {post.authorName.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-semibold text-gray-900 text-sm">{post.authorName}</p>
              <p className="text-xs text-gray-500 flex items-center gap-2">
                {formatDate(post.publishedAt)}
                <span aria-hidden="true">·</span>
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{post.readingTimeMinutes} min read</span>
                <span aria-hidden="true">·</span>
                <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" />{post.viewCount} views</span>
              </p>
            </div>
            <div className="ml-auto flex items-center gap-3">
              <LikeButton post={post} onRequireAuth={() => setShowLogin(true)} />
            </div>
          </div>

          {post.coverImageUrl && (
            <div className="relative aspect-[16/9] rounded-sm overflow-hidden my-12">
              <ImageWithFallback
                src={post.coverImageUrl}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <TiptapRenderer content={post.contentJson} />

          <div className="mt-12 pt-10 border-t border-gray-100">
            {post.tagNames.length > 0 && (
              <div className="flex flex-wrap gap-x-3 gap-y-1 mb-8">
                {post.tagNames.map(tag => (
                  <span key={tag} className="eyebrow text-gray-400">#{tag}</span>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-gray-500">Follow Hustlance</span>
              <SocialIconLinks />
            </div>
          </div>

          <CommentSection post={post} onRequireAuth={() => setShowLogin(true)} />
        </article>

        {related.length > 0 && (
          <section className="bg-hustlance-background py-20">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="font-editorial text-2xl text-gray-900 mb-10">Related Articles</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-14">
                {related.map(p => <PostCard key={p.id} post={p} />)}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer onNavigate={onNavigate} />

      <LoginModal isOpen={showLogin} onClose={() => setShowLogin(false)} onNavigate={onNavigate as (page: string) => void} />
    </div>
  );
}
